import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
  TextField,
  IconButton
} from '@mui/material';

import DeleteIcon from '@mui/icons-material/Delete';

import { format } from 'date-fns';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

const SentItemsModal = ({ open, handleClose, userData, setUserData, }) => {
  const { theme } = useTheme();
  const { updateSubUserItemQuantities, removeItemFromSubUser } = useAuth();

  const sentItems= userData?.sent_items || [];

  // Format price with 2 decimal places
  const formatPrice = (price) => {
    return `$${Number(price).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const styles = {
    modal: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '80%',
      bgcolor: theme.background.primary,
      boxShadow: 24,
      borderRadius: 2,
      p: 4,
      maxHeight: '70vh',
      overflow: 'auto'
    },
    text: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary
    },
    tr: {
      '&:last-child td, &:last-child th': {
        border: 0
      },
      backgroundColor: theme.background.secondary,
    },
    button: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      backgroundColor: theme.color.secondary,
      border: `1px solid ${theme.border.light}`,
    },
    tableInput: {
      width: '70px',
      '& .MuiInput-underline:before': { // Default bottom border
        borderBottomColor: theme.border.dark,
      },
      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { // Hover state
        borderBottomColor: theme.border.medium,
      },
      '& .MuiInput-underline:after': { // Focused state
        borderBottomColor: theme.color.secondary,
      },
    }
  };

  const handleRemoveItem = (removedItem) => {
    removeItemFromSubUser(userData.email, removedItem);
    const updatedSentItems = (userData?.sent_items || []).filter(item => item.sku !== removedItem.sku && item.date !== removedItem.date);
    setUserData({ ...userData, sent_items: updatedSentItems});
  }

  return (
    <Modal open={open} onClose={handleClose} >
      <Box sx={styles.modal}>
        <Typography sx={styles.text} variant="h5" component="h2" gutterBottom>
          Added Items for {userData?.name || 'User'}
        </Typography>
        <Typography sx={styles.text} variant="subtitle1" gutterBottom>
          Business: {userData?.business || 'N/A'}
        </Typography>
        <Typography sx={styles.text} variant="subtitle2" gutterBottom>
          Email: {userData?.email || 'N/A'}
        </Typography>

        <Box mt={3}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.color.primary, }}>
                  <TableCell sx={styles.text}>Image</TableCell>
                  <TableCell sx={styles.text}>SKU</TableCell>
                  <TableCell sx={styles.text}>Title</TableCell>
                  <TableCell sx={styles.text} align="right">Quantity</TableCell>
                  <TableCell sx={styles.text} align="right">Un-Paid Quantity</TableCell>
                  <TableCell sx={styles.text} align="right">Cost</TableCell>
                  <TableCell sx={styles.text} align="right">Total</TableCell>
                  <TableCell sx={styles.text}>Date</TableCell>
                  <TableCell sx={styles.text}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sentItems.length > 0 ? (
                  sentItems.map((item, index) => (
                    <TableRow key={index} sx={styles.tr}>
                      <TableCell>
                        <Avatar
                          variant="rounded"
                          src={item.image}
                          alt={item.title}
                          sx={{ width: 60, height: 60 }}
                        />
                      </TableCell>
                      <TableCell sx={styles.text}>{item.sku}</TableCell>
                      <TableCell sx={{ ...styles.text, maxWidth: 200 }}>{item.title}</TableCell>
                      <TableCell sx={styles.text} align="right">
                        <TextField
                          type="number"
                          size="small"
                          variant="standard"
                          value={item.qty || 0}
                          onChange={(e) => {
                            const newQty = parseInt(e.target.value, 10) || 0;
                            updateSubUserItemQuantities(userData.email, item.sku, 'qty', newQty);
                            item.qty = newQty;
                          }}
                          sx={styles.tableInput}
                          slotProps={{
                            input: {
                              sx: {
                                fontFamily: theme.fontFamily,
                                color: theme.text.primary,
                                padding: '4px 8px',
                              },
                            },
                            htmlInput: {
                              step: 1,
                              min: 0,
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={styles.text} align="right">
                        <TextField
                          type="number"
                          size="small"
                          variant="standard"
                          value={item.unpaidQty || 0}
                          onChange={(e) => {
                            const newUnpaidQty = parseInt(e.target.value, 10) || 0;
                            updateSubUserItemQuantities(userData.email, item.sku, 'unpaidQty', newUnpaidQty);
                            item.unpaidQty = newUnpaidQty;
                          }}
                          sx={styles.tableInput}
                          slotProps={{
                            input: {
                              sx: {
                                fontFamily: theme.fontFamily,
                                color: theme.text.primary,
                                padding: '4px 8px',
                              },
                            },
                            htmlInput: {
                              step: 1,
                              min: 0,
                              max: item.qty,
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={styles.text} align="right">{formatPrice(item.cost)}</TableCell>
                      <TableCell sx={styles.text} align="right">{formatPrice(item.cost * item.qty)}</TableCell>
                      <TableCell sx={styles.text}>{formatDate(item.date)}</TableCell>
                      <TableCell sx={{ ...styles.text, maxWidth: 30 }}>
                        <IconButton onClick={() => handleRemoveItem(item)}>
                          <DeleteIcon sx={{ color: theme.accent.error, }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{ backgroundColor: theme.background.secondary, }}>
                    <TableCell sx={styles.text} colSpan={9} align="center">No sent items found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button onClick={handleClose} variant="contained" sx={styles.button}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SentItemsModal;